import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FiPlus, FiEye, FiDownload, FiPrinter } from 'react-icons/fi';
import { getProducts, generateQrBatch, getQrBatches, getBatchQrCodes, getQrStats } from '../services/api';

const QrCodes: React.FC = () => {
  const [batches, setBatches] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showGenerate, setShowGenerate] = useState(false);
  const [showBatchDetail, setShowBatchDetail] = useState<any>(null);
  const [batchCodes, setBatchCodes] = useState<any[]>([]);
  const [genForm, setGenForm] = useState({
    productId: 0, quantity: 100, labourerCash: 0, dealerPoints: 0, contractorPoints: 0
  });

  useEffect(() => { loadData(); }, [page]);

  const loadData = async () => {
    try {
      const [batchRes, statsRes] = await Promise.all([getQrBatches(page), getQrStats()]);
      setBatches(batchRes.data.data);
      setTotal(batchRes.data.totalCount);
      setStats(statsRes.data.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const openGenerate = async () => {
    try {
      const res = await getProducts(1, 100);
      setProducts(res.data.data);
      setShowGenerate(true);
    } catch (err) { console.error(err); }
  };

  const handleProductChange = (pid: number) => {
    const p = products.find((x: any) => x.productId === pid);
    if (p) {
      setGenForm({
        productId: pid, quantity: genForm.quantity,
        labourerCash: p.labourerCash, dealerPoints: p.dealerPoints, contractorPoints: p.contractorPoints
      });
    }
  };

  const handleGenerate = async () => {
    if (!genForm.productId) { toast.error('Select a product'); return; }
    try {
      const res = await generateQrBatch(genForm);
      toast.success(res.data.message);
      setShowGenerate(false);
      loadData();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to generate');
    }
  };

  const viewBatch = async (batch: any) => {
    try {
      const res = await getBatchQrCodes(batch.batchNumber);
      setBatchCodes(res.data.data);
      setShowBatchDetail(batch);
    } catch (err) { console.error(err); }
  };

  const exportCsv = () => {
    if (!batchCodes.length) return;
    const csv = 'QR Code,Status\n' + batchCodes.map((c: any) => `${c.qrUuid},${c.status}`).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${showBatchDetail.batchNumber}.csv`;
    a.click();
  };

  const openPrintLabels = (batch: any) => {
    window.open(
      `/console/qr-codes/${encodeURIComponent(batch.batchNumber)}/labels`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const totalPages = Math.ceil(total / 20);

  return (
    <div>
      {stats && (
        <div className="stat-grid">
          <div className="stat-card">
            <div className="stat-info"><h3>{stats.total}</h3><p>Total QR Codes</p></div>
          </div>
          <div className="stat-card">
            <div className="stat-info"><h3>{stats.active}</h3><p>Available</p></div>
          </div>
          <div className="stat-card">
            <div className="stat-info"><h3>{stats.redeemed}</h3><p>Redeemed</p></div>
          </div>
        </div>
      )}

      <div className="flex-between mb-20">
        <h3>QR Batches</h3>
        <button className="btn btn-primary" onClick={openGenerate}><FiPlus /> Generate QR Batch</button>
      </div>

      <div className="card">
        {loading ? <div className="loading">Loading...</div> : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Batch</th><th>Product</th><th>Quantity</th><th>Used</th>
                  <th>Labourer (₹)</th><th>Dealer (pts)</th><th>Contractor (pts)</th>
                  <th>Created</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {batches.map((b) => (
                  <tr key={b.batchId}>
                    <td><strong>{b.batchNumber}</strong></td>
                    <td>{b.productName}</td>
                    <td>{b.quantity}</td>
                    <td>{b.usedCount} / {b.quantity}</td>
                    <td>₹{b.labourerCash}</td>
                    <td>{b.dealerPoints}</td>
                    <td>{b.contractorPoints}</td>
                    <td>{new Date(b.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div className="flex gap-10">
                        <button className="btn btn-sm btn-outline" onClick={() => viewBatch(b)}><FiEye /> View</button>
                        <button className="btn btn-sm btn-secondary" onClick={() => openPrintLabels(b)}><FiPrinter /> Print Labels</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {batches.length === 0 && <tr><td colSpan={9} className="text-center">No batches yet</td></tr>}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="pagination">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</button>
            <span>Page {page} of {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</button>
          </div>
        )}
      </div>

      {/* Generate Modal */}
      {showGenerate && (
        <div className="modal-overlay" onClick={() => setShowGenerate(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Generate QR Batch</h3>
              <button className="modal-close" onClick={() => setShowGenerate(false)}>&times;</button>
            </div>
            <div className="form-group">
              <label>Product *</label>
              <select className="form-control" value={genForm.productId}
                onChange={(e) => handleProductChange(parseInt(e.target.value))}>
                <option value={0}>Select Product</option>
                {products.map((p: any) => <option key={p.productId} value={p.productId}>{p.productName}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Quantity *</label>
              <input className="form-control" type="number" value={genForm.quantity}
                onChange={(e) => setGenForm({ ...genForm, quantity: parseInt(e.target.value) || 0 })} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Labourer Cash (₹)</label>
                <input className="form-control" type="number" value={genForm.labourerCash}
                  onChange={(e) => setGenForm({ ...genForm, labourerCash: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="form-group">
                <label>Dealer Points</label>
                <input className="form-control" type="number" value={genForm.dealerPoints}
                  onChange={(e) => setGenForm({ ...genForm, dealerPoints: parseInt(e.target.value) || 0 })} />
              </div>
              <div className="form-group">
                <label>Contractor Points</label>
                <input className="form-control" type="number" value={genForm.contractorPoints}
                  onChange={(e) => setGenForm({ ...genForm, contractorPoints: parseInt(e.target.value) || 0 })} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowGenerate(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleGenerate}>Generate</button>
            </div>
          </div>
        </div>
      )}

      {/* Batch Detail Modal */}
      {showBatchDetail && (
        <div className="modal-overlay" onClick={() => setShowBatchDetail(null)}>
          <div className="modal" style={{ minWidth: 600 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Batch: {showBatchDetail.batchNumber}</h3>
              <button className="modal-close" onClick={() => setShowBatchDetail(null)}>&times;</button>
            </div>
            <div className="flex-between mb-10">
              <span>{batchCodes.length} QR codes</span>
              <div className="flex gap-10">
                <button className="btn btn-sm btn-secondary" onClick={exportCsv}><FiDownload /> Export CSV</button>
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => openPrintLabels(showBatchDetail)}
                >
                  <FiPrinter /> Print Labels
                </button>
              </div>
            </div>
            <div className="table-container" style={{ maxHeight: 400, overflowY: 'auto' }}>
              <table>
                <thead><tr><th>#</th><th>QR Code</th><th>Status</th><th>Scanned At</th></tr></thead>
                <tbody>
                  {batchCodes.map((c: any, i: number) => (
                    <tr key={c.qrId}>
                      <td>{i + 1}</td>
                      <td><code>{c.qrUuid}</code></td>
                      <td><span className={`badge ${c.status === 'active' ? 'badge-success' : 'badge-danger'}`}>{c.status}</span></td>
                      <td>{c.scannedAt ? new Date(c.scannedAt).toLocaleString() : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QrCodes;
